/**
 * Disk kullanımı kancası (ADR-014).
 *
 * ## Neden ayrı kanca?
 *
 * Disk dolumu **yavaş** bir olaydır ama sonucu acildir: disk %95 doluyken
 * kullanıcı ancak oynatma durduğunda fark eder. Bu yüzden durum her ekranda
 * görünür olmalı — kanca tek noktadan okur, her ekran kendi kopyasını
 * çekmez.
 *
 * ## Sıklık
 *
 * 30 saniye (katman yoklamasıyla aynı). Disk 30 saniyede bir "doldur" ya da
 * "boşalmaz"; bu sıklık hem yeterli hem de sunucuyu yormaz.
 */
import type { DiskStatus } from './api.ts';
import { api } from './api.ts';
import { type PolledValue, usePolledValue } from './usePolledValue.ts';

/** Oturum yokken kullanılan nötr durum (hiçbir istek atılmaz). */
const IDLE_STATUS: DiskStatus = {
  filesystem: null,
  transcode: { path: '', usedBytes: 0, limitBytes: null, ratio: null, state: 'ok' },
  remoteCache: null,
  state: 'ok',
  messages: [],
};

/**
 * Disk durumunu periyodik okur.
 *
 * @param enabled Oturum açık mı? Kapalıyken **hiç istek atılmaz**
 *                (giriş ekranında 401 alıp hata göstermemek için).
 */
export function useDiskStatus(enabled = true): PolledValue<DiskStatus> {
  const polled = usePolledValue(async () => {
    if (!enabled) return IDLE_STATUS;
    return api.disk();
  });

  return { ...polled, value: enabled ? polled.value : null };
}

/** Bayt → insan dili ("12.3 GB"). */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const decimals = value >= 100 || unit === 0 ? 0 : 1;
  return `${value.toFixed(decimals)} ${units[unit]}`;
}

export type { PolledValue };
